import React, { useState } from "react";

import { Pagination } from "@performyard/py-components";

import FilterChips from "../../../components/FilterChips";
import PageHeading from "../../../components/PageHeading";
import CompanyMeetingsCoverageFilterPanel from "./CompanyMeetingsCoverageFilterPanel";
import CompanyMeetingsCoverageTable from "./CompanyMeetingsCoverageTable";

import ErrorPage from "../../../pages/Error";

import { useEmployeeMeetingStatsSearch } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import {
	type EmployeeMeetingStatsSortableFields,
	EmployeeMeetingStatsSortableFieldsEnum,
} from "../../../api/services/defs/internal-api";

import { convertFiltersToFilterChips, getEmployeeMeetingStatsSearchServiceFilters } from "../utils";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { COMPANY_MEETINGS_NAV_LINKS } from "../../CompanyMeetings/navigationLinks";
import { config } from "../config";
import { CompanyMeetingsCoverageFilters } from "../schema";

const PAGE_TITLE = "Meeting Coverage";

const CompanyMeetingsCoverage = () => {
	const isNewNav = useIsNewNavigation();
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	const { value: ffTeamMeetings } = useFeatureFlagQuery("ff-is-team-meetings-enabled", false);

	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<CompanyMeetingsCoverageFilters>({
		defaultSortName: config.defaultSortName,
		defaultSortDirection: config.defaultSortDirection,
		sortableFields: EmployeeMeetingStatsSortableFieldsEnum.options,
		defaultPageNumber: config.defaultPageNumber,
	});

	const {
		data: rows = [],
		isFetching,
		totalCount = 0,
	} = useEmployeeMeetingStatsSearch({
		...getEmployeeMeetingStatsSearchServiceFilters(filters, debouncedSearchValue),
		limit: config.pageSize,
		skip: (pageNumber - 1) * config.pageSize,
		sort: { name: sort.name as EmployeeMeetingStatsSortableFields, direction: sort.direction },
	});

	// Wait for the meetings feature flag to load, then show access denied if disabled.
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	const handleColumnHeaderClick = (column: { name: string; direction: "asc" | "desc" }) => {
		setSort(column);
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search employees..."
			onFilterButtonPress={() => setIsFilterPanelOpen(true)}
			activeFiltersCount={convertFiltersToFilterChips(filters).length}
		/>
	);

	const toolbarInformationRow = (
		<FilterChips
			filters={convertFiltersToFilterChips(filters)}
			label={convertFiltersToFilterChips(filters).length > 0 ? "Filters applied: " : ""}
			onDeleteFilter={(id) => {
				const currentFilters = { ...filters };
				delete currentFilters[id as keyof CompanyMeetingsCoverageFilters];
				setFilters({ ...currentFilters });
			}}
		/>
	);

	const filterPanel = (
		<CompanyMeetingsCoverageFilterPanel
			isOpen={isFilterPanelOpen}
			onClose={() => setIsFilterPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const mainContent = (
		<div className="space-y-6">
			<CompanyMeetingsCoverageTable
				rows={rows}
				isFetching={isFetching ?? false}
				showTeamMeetings={ffTeamMeetings}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				handleColumnHeaderClick={handleColumnHeaderClick}
			/>
			{totalCount > 0 && (
				<div className="mx-auto flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalCount / config.pageSize)}
						onPageChange={(page: number) => setPageNumber(page)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			mainContent={mainContent}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			navigationLinks={COMPANY_MEETINGS_NAV_LINKS}
			activeNavigationLinkKey="meeting-coverage"
			drawer={filterPanel}
		/>
	);
};

export default CompanyMeetingsCoverage;
